import React from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIZES = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-7 w-7 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-2xl',
};

// 8 cohesive tints picked deterministically by hash of the seed (name or id).
// Each entry uses semi-transparent backgrounds so it reads well in both
// light & dark themes, with a matching foreground tone that has good contrast.
const COLOR_PALETTE = [
  'bg-rose-500/15 text-rose-600 dark:text-rose-300',
  'bg-amber-500/15 text-amber-600 dark:text-amber-300',
  'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300',
  'bg-sky-500/15 text-sky-600 dark:text-sky-300',
  'bg-violet-500/15 text-violet-600 dark:text-violet-300',
  'bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300',
  'bg-teal-500/15 text-teal-600 dark:text-teal-300',
  'bg-orange-500/15 text-orange-600 dark:text-orange-300',
];

function hashString(s) {
  // Simple djb2-style hash, stable across renders.
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * Renders a person avatar — image when `picture` is set, initials fallback otherwise.
 * Pass `firstName`, `lastName`, optional `picture` (path or full URL),
 * and `size` (xs|sm|md|lg|xl, default md).
 *
 * Initials background is deterministically picked from an 8-color palette
 * keyed off the person's name+id, so the same person always gets the same
 * color and rows stay visually scannable.
 */
export const PersonAvatar = ({ firstName = '', lastName = '', picture, size = 'md', seed = '', className = '' }) => {
  const sizeCls = SIZES[size] || SIZES.md;
  const initials = `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase() || '?';

  if (picture) {
    const src = picture.startsWith('http') ? picture : `${API_URL}${picture}`;
    return (
      <img
        src={src}
        alt={`${firstName} ${lastName}`.trim() || 'avatar'}
        className={`${sizeCls.split(' ').slice(0, 2).join(' ')} rounded-full object-cover flex-shrink-0 ${className}`}
      />
    );
  }

  const colorSeed = `${seed || ''}${firstName}${lastName}` || 'default';
  const tone = COLOR_PALETTE[hashString(colorSeed) % COLOR_PALETTE.length];

  return (
    <div className={`${sizeCls} rounded-full ${tone} flex items-center justify-center flex-shrink-0 ${className}`}>
      <span className="font-semibold">{initials}</span>
    </div>
  );
};

export default PersonAvatar;
